import {DeleteOutlined, PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import {Button, Divider, InputRef, Popconfirm, Select, Space, Spin} from 'antd'
import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {CustomType, ToDoTypeOption} from '../../entities/journalRecord'
import {ToDoTypeEditButton} from './ToDoTypeEditButton'
import {ToDoTypeInput} from './ToDoTypeInput'
import {PostgrestError} from '@supabase/supabase-js'
import {EmEmoji} from '../emoji/EmEmoji'

export type ToDoTypeFormSelectorProps = {
  commonTypes: ToDoTypeOption[] // id=null
  userTypes: ToDoTypeOption[]
  chosenType?: ToDoTypeOption
  // null when the selection is cleared, or when the chosen type has been deleted
  onChange?: (newChosenType: ToDoTypeOption | null) => void
  createNewItem: (
    name: string,
    icon: string
  ) => Promise<{success: true; data: CustomType} | {success: false; error: PostgrestError}>
  editType: (editedType: CustomType) => Promise<
    | {
        success: true
        data: CustomType
      }
    | {
        success: false
        error: PostgrestError
      }
  >
  deleteUserType: (id: number) => void
  defaultWhenCleared?: ToDoTypeOption | null
  editable?: boolean
  isAllowClear?: boolean
}

function getOptionKey(item: ToDoTypeOption) {
  if (!item.id) {
    return `custom-${item.name}`
  } else {
    return item.id
  }
}

function todoTypeToSelectOption(item: ToDoTypeOption) {
  return {
    item: item,
    label: item,
    value: getOptionKey(item),
  }
}

export function ToDoTypeFormSelector({
  commonTypes,
  userTypes,
  chosenType,
  onChange = (newChosenType: ToDoTypeOption | null) => {},
  createNewItem,
  editType,
  deleteUserType,
  defaultWhenCleared = null,
  editable = true,
  isAllowClear = true,
}: ToDoTypeFormSelectorProps) {
  const intl = useIntl()
  //const revalidator = useRevalidator()

  //const [selectedType, setSelectedType] = useState(toSelectItem(chosenType))
  const selectOptions = [...commonTypes, ...userTypes]
  const [loading, setLoading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownSticky, setDropdownSticky] = useState(false)

  const [newUserType, setNewUserType] = useState<ToDoTypeOption>({
    id: null,
    icon: '🧑‍🌾',
    name: '',
  })

  const inputRef = useRef<InputRef>(null)
  const [name, setName] = useState('')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  let index = 0

  async function createUserType(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await createNewItem(
        newUserType.name || `New item ${index++}`,
        newUserType.icon
      )
      if (result.success) {
        //setSelectOptions([...selectOptions, result.data])
      }
    } finally {
      setName('')
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!chosenType?.id) {
      return
    }

    let found = false
    for (const userType of userTypes) {
      if (chosenType.id === userType.id) {
        onChange(userType)
        found = true
      }
    }

    if (!found) {
      onChange(defaultWhenCleared)
    }
  }, [userTypes])

  return (
    <Select
      optionLabelProp="label"
      open={dropdownOpen}
      onOpenChange={(newValue) => {
        if (dropdownSticky) {
          return
        }

        setDropdownOpen(newValue)
      }}
      style={{minWidth: 200, marginTop: 10, marginBottom: 10}}
      placeholder={intl.formatMessage({id: 'chooseType'})}
      value={chosenType ? todoTypeToSelectOption(chosenType) : undefined}
      labelInValue
      onChange={(_, newType) => {
        if (Array.isArray(newType)) {
          newType = newType[0]
        }
        if (loading) {
          return
        }
        onChange(newType ? newType.item : null)
      }}
      //defaultValue={todoTypeToSelectOption({id: null, name: 'general', icon: '📄'})}
      allowClear={isAllowClear}
      disabled={!editable}
      options={[...commonTypes, ...userTypes].map(todoTypeToSelectOption)}
      popupRender={(menu) => (
        <Spin spinning={loading} delay={300}>
          {menu}
          <Divider style={{margin: '8px 0'}} />
          <Space style={{padding: '0 8px 4px'}}>
            <ToDoTypeInput
              userType={newUserType}
              setUserType={setNewUserType}
              onEmojiDropdownVisibleChange={setDropdownSticky}
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={async (e) => {
                await createUserType(e)
                setNewUserType({id: null, icon: '🧑‍🌾', name: ''})
              }}
            >
              {/* {intl.formatMessage({id: 'add'})} */}
            </Button>
          </Space>
        </Spin>
      )}
      labelRender={(label) => {
        const {id, name, icon} = label.label as any as ToDoTypeOption
        return (
          <div
            className="type-item"
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                flexGrow: 1,
                flexShrink: 1,
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <EmEmoji id={icon} size="1em"></EmEmoji>
              {` ${id ? name : name ? intl.formatMessage({id: `eventType-${name}`}) : ''}`}
            </span>
          </div>
        )
      }}
      optionRender={(option) => {
        const item: ToDoTypeOption = option.data.item
        const {id, name, icon} = item
        return (
          <>
            <div
              className="type-item"
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  flexGrow: 1,
                  flexShrink: 1,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textWrap: 'wrap',
                  //whiteSpace: 'nowrap',
                  //textOverflow: 'ellipsis',
                }}
              >
                <EmEmoji id={icon} size="1em"></EmEmoji>
                {` ${id ? name : intl.formatMessage({id: `eventType-${name}`})}`}
              </span>
              {id !== null && (
                <span>
                  <ToDoTypeEditButton
                    userType={{...item, id}}
                    onEditType={async (editedType: CustomType) => {
                      const editingResult = await editType(editedType)

                      // setSelectOptions(
                      //   selectOptions.map((opt) => {
                      //     if (opt.id === editedType.id) {
                      //       return editedType
                      //     } else {
                      //       return opt
                      //     }
                      //   })
                      // )

                      return editingResult
                    }}
                  />
                  <span style={{flexGrow: 0, flexShrink: 0}} onClick={(e) => e.stopPropagation()}>
                    <Popconfirm
                      placement="bottom"
                      title={intl.formatMessage({id: 'areYouSureYouWantToDeleteThisType'})}
                      description={intl.formatMessage({
                        id: 'typeOfTheCorrespondingTasksAndEventsWillBeChangedToGeneral',
                      })}
                      icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                      onConfirm={() => {
                        setLoading(true)
                        deleteUserType(id)

                        // setSelectOptions(
                        //   selectOptions.filter((opt) => {
                        //     return opt.id != option.data.objectData.id
                        //   })
                        // )
                        //revalidator.revalidate()

                        setLoading(false)
                      }}
                      okText={intl.formatMessage({id: 'yes'})}
                      cancelText={intl.formatMessage({id: 'no'})}
                    >
                      <DeleteOutlined className="delete-collection" color="red" />
                    </Popconfirm>
                  </span>
                </span>
              )}
            </div>
          </>
        )
      }}
    />
  )
}
